import { useState } from 'react';
import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Stack,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
} from "@mui/material";
import { MobileDatePicker } from '@mui/x-date-pickers-pro';
import { fYYYYMMDD } from '../../utils/formatTime';


export default function AddCareerDialog({ isOpen, closeDialog, addCareer, hasWorkingCareer, canHide=true }) {
  const [companyName, setCompanyName] = useState('');
  const [companyNameError, setCompanyNameError] = useState(false);

  const [careerStartDate, setCareerStartDate] = useState('');
  const [noStartDateError, setNoStartDateError] = useState(false);

  const [careerEndDate, setCareerEndDate] = useState('');
  const [noEndDateError, setNoEndDateError] = useState(false);

  const [isCurrent, setIsCurrent] = useState(false);
  const [hidePeriod, setHidePeriod] = useState(false);

  const [careerDescription, setCareerDescription] = useState('');

  const handleCompanyName = (e) => {
    setCompanyName(e.target.value);
    setCompanyNameError(!e.target.value);
  };

  const handleIsCurrent = (e) => {
    setCareerEndDate('');
    setIsCurrent(e.target.checked);
  };

  const handleHidePeriod = (e) => {
    setHidePeriod(e.target.checked);
  };

  const handleStartDate = (date) => {
    setCareerStartDate(date);
    setNoStartDateError(!date);
  }

  const handleEndDate = (date) => {
    setCareerEndDate(date);
    setNoEndDateError(!date);
  }

  const close = async () => {
    setCompanyName('');
    setCompanyNameError(false);

    setCareerStartDate('');
    setNoStartDateError(false);

    setCareerEndDate('');
    setNoEndDateError(false);

    setIsCurrent(false);
    setHidePeriod(false);

    setCareerDescription('');

    closeDialog();
  };

  const add = async () => {
    if (!companyName || !careerStartDate || (!isCurrent && !careerEndDate)) {
      setCompanyNameError(!companyName);
      setNoStartDateError(!careerStartDate);
      setNoEndDateError(!careerEndDate && !isCurrent);
      return ;
    }

    addCareer({
      company_name: companyName,
      start_date: fYYYYMMDD(careerStartDate),
      end_date: isCurrent ? null : fYYYYMMDD(careerEndDate),
      is_hidden_career_period: hidePeriod,
      description: careerDescription,
    })
    close();
  }

  return (
    <Dialog open={isOpen} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ mb: 1 }}>
        경력 사항
      </DialogTitle>
      <DialogContent>
        <DialogContentText paragraph>
          재직한 혹은 재직 중인 회사 이름 및 년도와 월을 작성해주세요.
        </DialogContentText>
        <Stack spacing={3}>
          <Stack spacing={1} sx={{ width: 1 }}>
            <Typography variant="subtitle2">
              회사 이름
            </Typography>
            <TextField
              fullWidth
              value={companyName}
              error={companyNameError}
              helperText={companyNameError ? '회사이름을 입력해주세요' : ''}
              onChange={handleCompanyName}
            />
          </Stack>
          <Stack direction="row" spacing={2} >
            <Stack spacing={1} sx={{ width: 1 }}>
              <Typography variant="subtitle2">
                시작 연월
              </Typography>
              <MobileDatePicker
                closeOnSelect
                views={['year', 'month']}
                openTo={'year'}
                inputFormat="yyyy년 M월"
                value={careerStartDate}
                onChange={handleStartDate}
                minDate={new Date('1990-01-01')}
                maxDate={careerEndDate || new Date()}
                disableFuture
                renderInput={(params) => (
                  <TextField {...params} fullWidth error={noStartDateError} helperText={noStartDateError ? "날짜를 선택해주세요" : ""} />
                )}
              />
            </Stack>
            <Stack spacing={1} sx={{ width: 1 }}>
              <Typography variant="subtitle2">
                종료 연월
              </Typography>
              {isCurrent && (
                <TextField fullWidth disabled value="재직중" />
              )}
              {!isCurrent && (
              <MobileDatePicker
                closeOnSelect
                views={['year', 'month']}
                openTo={'year'}
                inputFormat="yyyy년 M월"
                value={careerEndDate}
                minDate={careerStartDate}
                maxDate={new Date()}
                onChange={handleEndDate}
                disableFuture
                renderInput={(params) => (
                  <TextField {...params} fullWidth error={noEndDateError} helperText={(noEndDateError && !isCurrent) ? "날짜를 선택해주세요" : ""} />
                )}
                disabled={isCurrent}
              />
              )}
            </Stack>
          </Stack>
          <Stack>
            <FormGroup sx={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              <FormControlLabel
                control={<Checkbox checked={isCurrent} onClick={handleIsCurrent} disabled={hasWorkingCareer} />}
                label="현재 이 업무로 근무 중"
                sx={{ color: "text.secondary", '& .MuiFormControlLabel-label' : {fontWeight: 'bold' } }}
              />
            </FormGroup>
            {canHide && (
            <FormGroup sx={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              <FormControlLabel
                control={<Checkbox checked={hidePeriod} onClick={handleHidePeriod} />}
                label="재직기간 비공개"
                sx={{ color: "text.secondary", '& .MuiFormControlLabel-label' : {fontWeight: 'bold' } }}
              />
            </FormGroup>
            )}
             <Stack spacing={1} sx={{ width: 1 }}>
              <Typography variant="subtitle2">
                설명 (선택)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={careerDescription}
                onChange={(e) => setCareerDescription(e.target.value)}
              />
              <Box sx={{ width: 1, textAlign: "end" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {careerDescription?.length === 0 ? "2000자 이내" : `${careerDescription?.length}/2000`}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          sx={{ px: 4 }}
          onClick={close}
          size="large"
        >
          취소
        </LoadingButton>
        <LoadingButton
          sx={{ px: 4 }}
          variant="contained"
          onClick={add}
          size="large"
          disabled={careerDescription?.length > 2000}
        >
          추가하기
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}